import type React from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useRecoilState } from 'recoil';
import {
  Box,
  Button,
  Card,
  Heading,
  Icon,
  Paragraph,
  TextButton,
  Toast,
  Upsell,
  useToast,
} from '@zillow/constellation-10';
import { IconPiggyBankFilled } from '@zillow/constellation-icons';
import { LeaseStatus } from '@zg-rentals/ts-rental-manager-api-client';
import { LeaseArtifactType } from '@zg-rentals/ts-leasing-client';
import { isSigningCompleteModalOpenState } from '../../../atoms';

import SummarizeTheLease from './SelfServeView/SummarizeTheLease';
import SelfServeActionContainer from './SelfServeView/SelfServeActionContainer';
import { SUMMARY_VIEW_SUBTYPES } from '../../models/Lease';
import { Lease, Property, Summary } from '../../models';
import {
  useLeaseSummaryQuery,
  useLeasingStatusQuery,
  useMarkAsSignedMutation,
  usePropertyInfoQuery,
  useUploadedFileDetailsQuery,
  useWebAnalyticsForListingQuery,
} from '../../utils/queries';
import { LeaseTypes } from '../../models/Types';
import UploadedLease from './SelfServeView/UploadedLease';
import DocumentPreviewSelector from '../../components/DocumentPreviewSelector';
import SignedSelfUploadSigningCompleteModal from './ListView/LeaseSignPreview/SignedSelfUploadSigningCompleteModal';
import { useLeasingUrlParams } from '../../hooks';

type Props = {
  lease: Lease;
};

export function SelfServeView({ lease }: Props) {
  const { leaseId, propertyId } = useLeasingUrlParams();
  const history = useHistory();
  const { enqueueToast } = useToast();

  const queryParams = new URLSearchParams(window.location.search);
  const nestedViewType = queryParams.get('nestedView');
  const [userHasStartedWizard, setUserHasStartedWizard] = useState(null);
  const [isLeaseFinalized, setIsLeaseFinalized] = useState(false);
  const [, setIsSigningCompleteModalOpen] = useRecoilState(isSigningCompleteModalOpenState);

  const markAsSignedMutation = useMarkAsSignedMutation();

  const leasingStatusQuery = useLeasingStatusQuery(leaseId, { enabled: !!leaseId });

  useEffect(() => {
    setIsSigningCompleteModalOpen(false);
  }, [setIsSigningCompleteModalOpen]);

  const uploadedFilesQuery = useUploadedFileDetailsQuery(leaseId);
  const hasUploadedAFile = uploadedFilesQuery?.data?.length > 0;

  const propertyQuery = usePropertyInfoQuery(propertyId);
  const property = propertyQuery.data ? new Property({ ...propertyQuery.data, propertyId }) : null;

  useWebAnalyticsForListingQuery(propertyId, { enabled: !!propertyId });

  const leaseSummaryQuery = useLeaseSummaryQuery(leaseId, LeaseArtifactType.LEASE);

  const summaryText = leaseSummaryQuery.data?.summaryText || '';
  const summaryActions = leaseSummaryQuery.data?.summaryActions || [];
  const summary = useMemo(
    () => (leaseSummaryQuery.data?.summary ? new Summary({ sectionCategories: leaseSummaryQuery.data.summary }) : null),
    [leaseSummaryQuery.data?.summary],
  );

  const getUserHasStartedWizard = useCallback(() => {
    if (leasingStatusQuery.data) {
      const userStarted = leasingStatusQuery.data.hasVisited || leasingStatusQuery.data.hasPrevious;
      setUserHasStartedWizard((prev) => prev || userStarted);
    }
  }, [leasingStatusQuery.data]);

  useEffect(() => {
    getUserHasStartedWizard();
  }, [getUserHasStartedWizard]);

  function onDocumentClose() {
    history.push(Lease.getSelfServePath(leaseId));
  }

  function onDocumentError(body: string) {
    onDocumentClose();
    enqueueToast(<Toast tone="critical">{body}</Toast>);
  }

  function renderNestedView() {
    switch (nestedViewType) {
      case SUMMARY_VIEW_SUBTYPES.PREVIEW:
        return (
          <DocumentPreviewSelector
            leaseId={leaseId}
            lease={lease}
            property={property}
            onClose={onDocumentClose}
            onError={onDocumentError}
          />
        );
      default:
        return null;
    }
  }

  function renderUploadedLease() {
    return <UploadedLease canDelete={lease?.isEditable()} lease={lease} />;
  }

  async function finalizeLease() {
    await markAsSignedMutation.mutateAsync({
      postData: {
        leaseArtifactType: LeaseArtifactType.LEASE,
        leaseArtifactId: lease.id as unknown as number,
      },
    });
    setIsLeaseFinalized(true);
  }

  function onSignedSelfUploadSigningCompleteModalClose() {
    setIsLeaseFinalized(false);
  }

  function renderSignedSelfUploadContainer(): React.ReactNode {
    if (
      lease?.status === LeaseStatus.EXPIRED ||
      lease?.status === LeaseStatus.TERMINATED ||
      lease?.status === LeaseStatus.ACTIVE ||
      lease?.status === LeaseStatus.SIGNING_COMPLETE
    ) {
      return null;
    }
    if (lease?.status === LeaseStatus.COMPLETE_DRAFT && hasUploadedAFile) {
      return (
        <Card tone="soft" elevated={false} outlined={false}>
          <Box
            css={{
              display: 'flex',
              flexWrap: 'wrap',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: 'tight',
            }}
          >
            <Paragraph>
              If all the information is correct, continue to the next step. You will no longer be able to make changes
              after finalizing.
            </Paragraph>
            <Button
              tone="brand"
              emphasis="filled"
              onClick={finalizeLease}
              loading={markAsSignedMutation.isPending}
              css={{ whiteSpace: 'nowrap' }}
            >
              Finalize lease
            </Button>
          </Box>
        </Card>
      );
    }
  }

  function onSetUpPayments(): void {
    window.location.href = property.getPaymentsDashboardPath(lease.encodedId);
  }

  const propertyAddress = property && property.streetAddress;
  const leaseStartDate = lease?.getFriendlyStartDate();
  const hasColandlord = lease?.hasColandlord();
  const disableCta = !summary || !summary.isSubmittable() || !hasUploadedAFile;
  const isSignedSelfUpload = lease?.leaseType === LeaseTypes.SIGNED_SELF_UPLOAD;

  return (
    <>
      {renderNestedView()}
      {lease && (lease.status === LeaseStatus.ACTIVE || lease.status === LeaseStatus.SIGNING_COMPLETE) && (
        <>
          <Heading level={3} css={{ textStyle: 'responsive.body-lg-bold', marginBottom: 'tighter' }}>
            Next step
          </Heading>
          <Upsell
            css={{ marginBottom: 'tight' }}
            header={<Heading level={4}>Collect move-in costs</Heading>}
            body={
              <Paragraph>
                Request payment of your move-in costs (which include the security deposit, prorated rent, and any
                move-in fees) through Zillow Payments.
              </Paragraph>
            }
            media={
              <Icon size="lg" role="img">
                <IconPiggyBankFilled />
              </Icon>
            }
            action={
              <TextButton tone="brand" onClick={onSetUpPayments}>
                Set up payments
              </TextButton>
            }
            tone="soft"
            elevated={false}
            closeable={false}
          />
        </>
      )}
      {lease && (
        <SignedSelfUploadSigningCompleteModal
          lease={lease}
          isOpen={isLeaseFinalized}
          onClose={onSignedSelfUploadSigningCompleteModalClose}
        />
      )}
      {summaryActions.length > 0 && userHasStartedWizard && !isSignedSelfUpload && (
        <Box css={{ marginBottom: 'tight' }}>
          <SelfServeActionContainer
            disableCta={disableCta}
            showDescription
            {...{ summaryText, summaryActions, lease, property, propertyAddress, leaseStartDate, hasColandlord }}
          />
        </Box>
      )}
      {renderUploadedLease()}

      <SummarizeTheLease
        leaseId={leaseId}
        lease={lease}
        userHasStartedWizard={userHasStartedWizard}
        setUserHasStartedWizard={setUserHasStartedWizard}
      />
      {summaryActions.length > 0 && userHasStartedWizard && (
        <>
          {isSignedSelfUpload ? (
            renderSignedSelfUploadContainer()
          ) : (
            <SelfServeActionContainer
              disableCta={disableCta}
              showDescription={false}
              {...{ summaryText, summaryActions, lease, property, propertyAddress, leaseStartDate, hasColandlord }}
            />
          )}
        </>
      )}
    </>
  );
}

export default SelfServeView;
